import { z } from 'zod';
import type { Font, Styles } from '../graphql/query/getRetailerSettings';

const fontFaceSchema = z.object({
  fontFamily: z
    .string()
    .max(100)
    .regex(/^[\w\s-]+$/),
  fontWeight: z.enum(['300', 'normal', 'bold']).nullish(),
  src: z.string(),
});

export const themeSchema = z.object({
  ctaColor: z.string().optional(),
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  textColor: z.string().optional(),
  fontFamily: z.string().optional(),
});

export type ThemeSchemaType = z.infer<typeof themeSchema>;

const fontEntrySchema = z.object({
  fontFamily: z.string().nullable(),
  fontWeight: z.string().nullable(),
  src: z.string().nullable(),
});

const themeColorsSchema = z.object({
  ctaColor: z.string().nullable(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  textColor: z.string().nullable(),
});

export const stylesSchema = z.object({
  font: z.array(fontEntrySchema),
  theme: themeColorsSchema.nullable(),
  themeVarOverrides: z.array(z.array(z.string()).nullable()).nullable(),
});

export const validateThemeData = (themeData: ThemeSchemaType) => {
  const result = themeSchema.safeParse(themeData);
  return result;
};

/**
 * Maps theme schema keys to their corresponding CSS custom properties.
 * Note: ctaColor is handled separately via generateCtaColorCss() to derive hover/pressed states.
 */
export const pdxThemeMapping: Partial<
  Record<keyof ThemeSchemaType, readonly string[]>
> = {
  primaryColor: [
    '--n-color-component-action-plain-content-default',
    '--n-color-border-focus-ring',
  ],
  secondaryColor: ['--n-color-component-action-secondary-fill-default'],
  textColor: ['--n-color-content-neutral-base'],
  fontFamily: ['--n-font-family-base'],
} as const;

/**
 * Generates CSS custom properties for CTA color with derived hover/pressed states
 * using CSS color-mix() to darken the base color progressively.
 */
const generateCtaColorCss = (ctaColor: string): string => {
  return [
    `--n-color-component-action-primary-fill-default: ${ctaColor};`,
    `--n-color-component-action-primary-fill-hover: color-mix(in srgb, ${ctaColor}, black 12%);`,
    `--n-color-component-action-primary-fill-pressed: color-mix(in srgb, ${ctaColor}, black 40%);`,
  ].join('\n  ');
};

const escapeFontFamily = (family: string): string => {
  const escaped = CSS.escape(family);
  return /\s/.test(family) ? `'${escaped}'` : escaped;
};

const generateFontFaceCss = (fontConfig: Font): string => {
  if (!fontConfig || fontConfig.length === 0) {
    return '';
  }

  return fontConfig
    .map((font) => {
      const parsed = fontFaceSchema.safeParse(font);
      if (!parsed.success) {
        // eslint-disable-next-line no-console
        console.warn('Rejected unsafe font‑face payload', parsed.error.issues);
        return null;
      }

      const { fontFamily, fontWeight, src } = parsed.data;

      const escapedFamily = escapeFontFamily(fontFamily);

      const weightLine = fontWeight ? `  font-weight: ${fontWeight};\n` : '';

      return (
        `@font-face {\n` +
        `  font-family: ${escapedFamily};\n` +
        weightLine +
        `  font-display: swap;\n` +
        `  src: ${src}\n` +
        `}`
      );
    })
    .filter((rule): rule is string => rule !== null)
    .join('\n\n');
};

export const headerColorsSchema = z.object({
  backgroundColor: z.string().nullish(),
  textColor: z.string().nullish(),
});

export type HeaderColors = z.infer<typeof headerColorsSchema>;

/**
 * Replace the page favicon with a branded one from the retailer's MX assets.
 * Creates or updates the `<link rel="icon">` element in the document head.
 * Only allows https: URLs to prevent injection of unexpected protocols.
 */
export const injectFavicon = (href: string) => {
  try {
    const url = new URL(href);
    if (url.protocol !== 'https:') return;
  } catch {
    return;
  }

  document
    .querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    )
    .forEach((el) => el.remove());

  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href;
  document.head.appendChild(link);
};

export const injectRetailerTheme = ({
  styles,
  headerColors,
  options,
}: {
  options?: {
    id?: string;
    layer?: string;
    retailer?: string | null;
  };
  styles: Styles;
  headerColors?: HeaderColors;
}) => {
  const { theme } = styles;
  const { id = 'retailerTheme', layer = 'retailer', retailer } = options || {};

  const themeStyleTag = document.getElementById(id);

  if (
    themeStyleTag &&
    themeStyleTag.getAttribute('data-retailer') === retailer
  ) {
    return;
  }

  const { data: validThemeData, success: isThemeDataValid } = validateThemeData(
    theme as ThemeSchemaType,
  );

  const fontFaceCss = generateFontFaceCss(styles?.font);

  // MX convention: font[0] = primary (heading/display), font[1] = secondary (body)
  // If only one font is provided, it applies to everything via --n-font-family-base.
  // If two fonts are provided, secondary → body, primary → headings.
  let baseFontFamily: string | null = null;
  let headingFontFamily: string | null = null;

  if (styles?.font && styles.font.length > 0) {
    const fontFamilies = styles.font
      .filter((f) => f.fontFamily)
      .map((f) => f.fontFamily as string);

    if (fontFamilies.length >= 2) {
      headingFontFamily = fontFamilies[0] ?? null;
      baseFontFamily = fontFamilies[1] ?? null;
    } else if (fontFamilies.length === 1) {
      baseFontFamily = fontFamilies[0] ?? null;
    }
  }

  const hasValidThemeEntries =
    isThemeDataValid &&
    validThemeData &&
    Object.keys(validThemeData).length > 0;
  const hasFontFaces = fontFaceCss.length > 0;
  const hasBaseFontFamily = baseFontFamily !== null;

  if (!hasValidThemeEntries && !hasFontFaces && !hasBaseFontFamily) {
    // eslint-disable-next-line no-console
    console.error(
      'No valid theme data or font faces to inject for retailer:',
      retailer ?? 'unknown',
    );
    return;
  }

  let cssCustomProperties = '';
  if (hasValidThemeEntries && validThemeData) {
    const standardProperties = Object.entries(validThemeData)
      .filter(([key]) => key !== 'fontFamily' && key !== 'ctaColor')
      .map((entry) => {
        if (entry[1] === null) return null;

        const [key, value] = entry as [keyof ThemeSchemaType, string];
        let pdxThemeMappedValue = pdxThemeMapping[key];

        // Temporary StitchFix override – remove when redundant
        if (
          pdxThemeMappedValue &&
          key === 'textColor' &&
          retailer === 'stitchfix' &&
          !pdxThemeMappedValue.includes(
            '--n-color-component-action-primary-content-default',
          )
        ) {
          pdxThemeMappedValue = [
            ...pdxThemeMappedValue,
            '--n-color-component-action-primary-content-default',
          ];
        }

        return pdxThemeMappedValue && pdxThemeMappedValue.length > 0
          ? pdxThemeMappedValue
              .map((cssCustomProp) => `${cssCustomProp}: ${value};`)
              .join('\n  ')
          : null;
      })
      .filter((prop): prop is string => prop !== null)
      .join('\n  ');

    // Generate CTA color with derived hover/pressed states using color-mix()
    const ctaColorCss = validThemeData.ctaColor
      ? generateCtaColorCss(validThemeData.ctaColor)
      : '';

    cssCustomProperties = [standardProperties, ctaColorCss]
      .filter(Boolean)
      .join('\n  ');
  }

  const fontFamilyCssCustomProperty =
    hasBaseFontFamily && pdxThemeMapping.fontFamily?.[0]
      ? `${pdxThemeMapping.fontFamily[0]}: ${escapeFontFamily(baseFontFamily!)};`
      : '';

  const headingFontCssCustomProperty = headingFontFamily
    ? `--n-font-family-heading: ${escapeFontFamily(headingFontFamily)};`
    : '';

  const headerColorCss = [
    headerColors?.backgroundColor &&
      `--n-color-branded-header-bg: ${headerColors.backgroundColor};`,
    headerColors?.textColor &&
      `--n-color-branded-header-text: ${headerColors.textColor};`,
  ]
    .filter(Boolean)
    .join('\n  ');

  const style = document.createElement('style');
  style.id = id;
  style.setAttribute('data-layer', layer);
  if (retailer) style.setAttribute('data-retailer', retailer);

  let rootBlockContent = [
    cssCustomProperties,
    fontFamilyCssCustomProperty,
    headingFontCssCustomProperty,
    headerColorCss,
  ]
    .filter(Boolean)
    .join('\n  ');

  // Add themeVarOverrides as CSS custom properties in :root
  let themeVarOverridesBlock = '';
  if (
    Array.isArray(styles.themeVarOverrides) &&
    styles.themeVarOverrides.length > 0
  ) {
    themeVarOverridesBlock = styles.themeVarOverrides
      .map((pair) => {
        if (
          Array.isArray(pair) &&
          pair.length === 2 &&
          typeof pair[0] === 'string' &&
          typeof pair[1] === 'string'
        ) {
          const [cssVar, value] = pair;
          return `${cssVar}: ${value};`;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n  ');
  }

  // Combine all :root custom properties
  const allRootBlockContent = [rootBlockContent, themeVarOverridesBlock]
    .filter(Boolean)
    .join('\n  ');

  let cssContent = `@layer ${layer} {\n`;
  if (hasFontFaces) {
    cssContent += `\n${fontFaceCss}\n\n`;
  }
  if (allRootBlockContent) {
    cssContent += `  :root {\n  ${allRootBlockContent} \n  }\n`;
    cssContent += `  html { font-size: var(--n-font-size-base, 1rem); }\n`;
  }
  cssContent += `}`;

  style.textContent = cssContent;

  document.head.appendChild(style);
};
